import type { ProgressCheckerManager } from './types'
import { isDOM } from '@app/shared/predicates'

import createProgressCheckerManager from './progress-checker-manager'

/* Polls a certain status update url to check if the job/task has finished
 *  Usage:
 *    In html, add data-behaviour='progress-checker' and data-progress-path=check_url
 *    In the controller at check_url, respond with json:
 *      {status: "processing"}
 *      {status: "processing", status_update: text_string} # updates nested data-behaviour='progress-checker-status'
 *      {status: "ready", html: html_string} # replaces the progress-checker DOM node with html_string
 *      {status: "ready", redirect: redirect_path} # Uses Turbolinks.visit(redirect_path) to redirect
 *      {status: *, global_updates: {'jquery query1': 'element replacement html', ..} # To replace arbitrary elements
 */

export default (): (() => void) => {
  let currentProgressManager: ProgressCheckerManager | null = null
  const unSubscribers: Array<() => void> = []

  const tearDownProgressManager = (): void => {
    if (currentProgressManager) {
      currentProgressManager.teardown()
    }
  }

  const initCurrentProgressManager = ({
    containerElements,
    containerGroups,
  }: {
    containerElements: HTMLElement[]
    containerGroups: Record<string, HTMLElement[]>
  }): void => {
    currentProgressManager = createProgressCheckerManager({
      containerElements,
    })

    for (const path in containerGroups) {
      const existingProgressChecker =
        currentProgressManager.findProgressCheckerByPath(path)

      if (!existingProgressChecker) {
        currentProgressManager.createProgressChecker(path)
      } else {
        for (const container of containerElements) {
          currentProgressManager.updateContainers(container)
        }
      }
    }
  }

  window.onDomLoad(($dom) => {
    const doc = $dom[0]

    if (!isDOM(doc)) {
      return
    }

    if (currentProgressManager) {
      currentProgressManager.teardown()
    }
    const containerElementsRaw = doc.querySelectorAll(
      "[data-behaviour~='progress-checker']"
    )

    const containerElements = [...containerElementsRaw] as HTMLElement[]

    const containerGroups = containerElements.reduce((acc, el) => {
      const path = el.dataset.progressPath as string

      if (Array.isArray(acc.path)) {
        acc[path].push(el)
      } else {
        acc[path] = [el]
      }

      return acc
    }, {} as Record<string, HTMLElement[]>)

    initCurrentProgressManager({
      containerElements,
      containerGroups,
    })

    doc.addEventListener('turbolinks:visit', tearDownProgressManager)

    const handleVisibilityChange = (): void => {
      // Remove the stale progress manager no matter what
      tearDownProgressManager()

      if (!doc.hidden) {
        initCurrentProgressManager({
          containerElements,
          containerGroups,
        })
      }
    }

    doc.addEventListener('visibilitychange', handleVisibilityChange)

    unSubscribers.push(() => {
      doc.removeEventListener('turbolinks:visit', tearDownProgressManager)
    })

    unSubscribers.push(() => {
      doc.removeEventListener('visibilitychange', handleVisibilityChange)
    })
  })

  return (): void => {
    currentProgressManager?.teardown()
    unSubscribers.forEach((unSubscriber) => {
      unSubscriber()
    })
  }
}
